import React, { useEffect, useImperativeHandle, useState } from 'react';
import { isBrandsPaymentLive } from '../../Common/hooks/useBrandPaymentsLive';
import { PaymentInputsWrapper, usePaymentInputs } from 'react-payment-inputs';
import images from 'react-payment-inputs/images';
import { css } from 'styled-components';
import { Box, HStack, Image, Text, VStack } from 'native-base';
import * as Sentry from '@sentry/browser';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons/faCircleCheck';
import { Icon } from '../../Common/components/Icon';
import { useConfig } from '../../Common/hooks/useConfig';
import { PaymentComponentType } from '../types/types';

const PaymentEWay = React.forwardRef((props: PaymentComponentType, ref) => {
  const livePayments = isBrandsPaymentLive();

  const [cardNumber, setCardNumber] = useState();
  const [cardExp, setCardExp] = useState();
  const [cardCCV, setCardCCV] = useState();

  const { configForBrand } = useConfig();
  const config = configForBrand(props.brand);

  const {
    wrapperProps,
    getCardImageProps,
    getCardNumberProps,
    getExpiryDateProps,
    getCVCProps,
    meta,
  } = usePaymentInputs();

  const [cardHolderName, setCardHolderName] = useState('');

  const [isCardValid, setIsCardValid] = useState(false);

  const cardHolderNameValid = cardHolderName?.length >= 1;

  const [clicked, setClicked] = useState(false);

  useEffect(() => {
    const isValid = meta?.error === undefined;
    setIsCardValid(isValid);
    props.setCardCompleted(isValid);
  }, [meta?.error]);

  const key = config?.paymentGatewayAPIPublicKey;

  useImperativeHandle(ref, () => ({
    confirmPayment: () => {
      const promise = new Promise((accept, reject) => {
        setClicked(true);

        if (!cardHolderNameValid) {
          reject();
          return;
        }

        const cardExpSplit = cardExp.split('/');
        const cardExpMonth = cardExpSplit[0].trim();
        const cardExpYear = cardExpSplit[1].trim();

        const payload = JSON.stringify({
          type: 'newcard',
          card: eCrypt.encryptValue(cardNumber, key),
          ccv: eCrypt.encryptValue(cardCCV, key),
          expMonth: cardExpMonth,
          expYear: cardExpYear,
          cardType: meta?.cardType?.displayName,
          cardHolder: cardHolderName,
          gateway: 'eway',
        });

        Sentry.addBreadcrumb({
          category: 'consult',
          message: 'Encrypted payment values',
          level: 'info',
        });

        accept(payload);
      });

      return promise;
    },
  }));

  return (
    <VStack space={1} opacity={props.disabled ? 0.5 : 1}>
      <HStack space={2} alignItems={'center'}>
        <Text color={'black'} fontWeight={'600'} flex={1}>
          Your Payment Card
        </Text>
        <Image
          borderRadius={'sm'}
          h={8}
          w={10}
          source={require('../../Assets/PaymentMethods/1.png')}
        />
        <Image
          borderRadius={'sm'}
          h={8}
          w={10}
          source={require('../../Assets/PaymentMethods/2.png')}
        />
        <Image
          borderRadius={'sm'}
          h={8}
          w={10}
          source={require('../../Assets/PaymentMethods/22.png')}
        />
      </HStack>

      <Box
        bg={clicked && !cardHolderNameValid ? 'red.200' : null}
        p={clicked && !cardHolderNameValid ? 2 : 0}
        borderRadius={'sm'}
      >
        <input
          style={{
            border: '1px solid rgb(212, 212, 212)',
            height: '32px',
            background: 'white',
            padding: '8px',
            'border-radius': '4px',
            'font-size': '16px',
          }}
          placeholder={'Card Holder Name'}
          value={cardHolderName}
          onChange={(e) => setCardHolderName(e.target.value)}
        />
      </Box>

      <PaymentInputsWrapper
        {...wrapperProps}
        styles={{
          errorText: {
            base: css`
              color: red;
              font-size: 14px;
            `,
          },
        }}
      >
        <svg {...getCardImageProps({ images })} />

        <input
          value={cardNumber}
          disabled={props.disabled}
          {...getCardNumberProps({
            onChange: (e) => {
              setCardNumber(e.target.value);
            },
          })}
        />

        <input
          value={cardExp}
          disabled={props.disabled}
          {...getExpiryDateProps({
            onChange: (e) => {
              setCardExp(e.target.value);
            },
          })}
        />

        <input
          value={cardCCV}
          disabled={props.disabled}
          {...getCVCProps({
            onChange: (e) => {
              setCardCCV(e.target.value);
            },
          })}
        />

        {isCardValid && (
          <Box position={'absolute'} right={0}>
            <Icon color={'green'} icon={faCircleCheck} />
          </Box>
        )}
      </PaymentInputsWrapper>
    </VStack>
  );
});

export default PaymentEWay;
